

'use client';

import React, { createContext, useContext, useEffect, useState, useMemo, ReactNode, useCallback } from 'react';
import { useAuth } from './auth-context';
import { subscribeSetting } from '@/lib/v3/reads';
import { setSetting } from '@/lib/v3/writes';
import { SPORT_ICONS } from '@/lib/types';

export type DebugDockSide = 'left' | 'right' | 'bottom' | 'top';
type SortOrder = 'asc' | 'desc';

interface DebugContextType {
  logs: string[];
  addLog: (message: string) => void;
  clearLogs: () => void;
  showDebug: boolean;
  setShowDebug: (show: boolean) => void;
  debugDock: DebugDockSide;
  setDebugDock: (side: DebugDockSide) => void;
  sortOrder: SortOrder;
  toggleSortOrder: () => void;
  randomizeCalendarIcons: boolean;
  setRandomizeCalendarIcons: (randomize: boolean) => void;
  randomizeIconSet: string[];
  setRandomizeIconSet: (iconSet: string[]) => void;
  /** When true (and user is superadmin), components wrapped in ComponentBounds show a border and label. Toggle in Admin → Developer. */
  showComponentBounds: boolean;
  setShowComponentBounds: (show: boolean) => void;
}

const DebugContext = createContext<DebugContextType | undefined>(undefined);

export const DebugProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [logs, setLogs] = useState<string[]>([]);
  const [showDebug, setShowDebugState] = useState(false);
  const [debugDock, setDebugDockState] = useState<DebugDockSide>('bottom');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [randomizeCalendarIcons, setRandomizeCalendarIconsState] = useState(false);
  const [randomizeIconSet, setRandomizeIconSetState] = useState<string[]>(SPORT_ICONS.map(i => i.icon));
  const [showComponentBounds, setShowComponentBoundsState] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (user && !isInitialized) {
      const unsubscribe = subscribeSetting(user.uid, 'debug', (data) => {
        if (data && (data.enabled !== undefined || data.dockSide !== undefined || data.showComponentBounds !== undefined)) {
          setShowDebugState(Boolean(data.enabled));
          setDebugDockState((data.dockSide as DebugDockSide) || 'bottom');
          setSortOrder((data.sortOrder as SortOrder) || 'desc');
          setRandomizeCalendarIconsState(Boolean(data.randomizeCalendarIcons));
          setRandomizeIconSetState(
            Array.isArray(data.randomizeIconSet) ? data.randomizeIconSet as string[] : SPORT_ICONS.map(i => i.icon),
          );
          setShowComponentBoundsState(Boolean(data.showComponentBounds));
        } else {
          const defaultSettings = {
            enabled: false,
            dockSide: 'bottom' as DebugDockSide,
            sortOrder: 'desc' as SortOrder,
            randomizeCalendarIcons: false,
            randomizeIconSet: SPORT_ICONS.map(i => i.icon),
            showComponentBounds: false,
          };
          setShowDebugState(defaultSettings.enabled);
          setDebugDockState(defaultSettings.dockSide);
          setSortOrder(defaultSettings.sortOrder);
          setRandomizeCalendarIconsState(defaultSettings.randomizeCalendarIcons);
          setRandomizeIconSetState(defaultSettings.randomizeIconSet);
          setShowComponentBoundsState(defaultSettings.showComponentBounds);
          void setSetting(user.uid, 'debug', defaultSettings);
        }
        setIsInitialized(true);
      });

      return () => unsubscribe();
    } else if (!user) {
        setIsInitialized(false);
        setShowDebugState(false);
        setShowComponentBoundsState(false);
        setLogs([]);
    }
  }, [user, isInitialized]);

  const addLog = useCallback((message: string) => {
    const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const fullMessage = `[${timestamp}] ${message}`;
    console.log(fullMessage); // Also log to browser console
    setLogs(prevLogs => [...prevLogs.slice(-100), fullMessage]); // Keep last 100 logs
  }, []);

  const clearLogs = () => setLogs([]);

  const setDebugSetting = useCallback(async (key: 'enabled' | 'dockSide' | 'sortOrder' | 'randomizeCalendarIcons' | 'randomizeIconSet' | 'showComponentBounds', value: unknown) => {
    if (user) {
      if (key === 'enabled') setShowDebugState(value as boolean);
      if (key === 'dockSide') setDebugDockState(value as DebugDockSide);
      if (key === 'sortOrder') setSortOrder(value as SortOrder);
      if (key === 'randomizeCalendarIcons') setRandomizeCalendarIconsState(value as boolean);
      if (key === 'randomizeIconSet') setRandomizeIconSetState(value as string[]);
      if (key === 'showComponentBounds') setShowComponentBoundsState(value as boolean);
      await setSetting(user.uid, 'debug', { [key]: value });
    }
  }, [user]);

  const toggleSortOrder = useCallback(() => {
    setDebugSetting('sortOrder', sortOrder === 'asc' ? 'desc' : 'asc');
  }, [sortOrder, setDebugSetting]);

  const setShowDebug = useCallback((show: boolean) => { void setDebugSetting('enabled', show); }, [setDebugSetting]);
  const setDebugDock = useCallback((side: DebugDockSide) => { void setDebugSetting('dockSide', side); }, [setDebugSetting]);
  const setRandomizeCalendarIcons = useCallback((randomize: boolean) => { void setDebugSetting('randomizeCalendarIcons', randomize); }, [setDebugSetting]);
  const setRandomizeIconSet = useCallback((iconSet: string[]) => { void setDebugSetting('randomizeIconSet', iconSet); }, [setDebugSetting]);
  const setShowComponentBounds = useCallback((show: boolean) => { void setDebugSetting('showComponentBounds', show); }, [setDebugSetting]);

  const value = useMemo<DebugContextType>(() => ({
    logs,
    addLog,
    clearLogs,
    showDebug,
    setShowDebug,
    debugDock,
    setDebugDock,
    sortOrder,
    toggleSortOrder,
    randomizeCalendarIcons,
    setRandomizeCalendarIcons,
    randomizeIconSet,
    setRandomizeIconSet,
    showComponentBounds,
    setShowComponentBounds,
  }), [
    logs,
    addLog,
    clearLogs,
    showDebug,
    setShowDebug,
    debugDock,
    setDebugDock,
    sortOrder,
    toggleSortOrder,
    randomizeCalendarIcons,
    setRandomizeCalendarIcons,
    randomizeIconSet,
    setRandomizeIconSet,
    showComponentBounds,
    setShowComponentBounds,
  ]);

  return <DebugContext.Provider value={value}>{children}</DebugContext.Provider>;
};

export const useDebug = () => {
  const context = useContext(DebugContext);
  if (context === undefined) {
    throw new Error('useDebug must be used within a DebugProvider');
  }
  return context;
};
